import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import pushNotificationManager from '../../services/push-notification-service';

const NotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = sessionStorage.getItem('isUserAuthenticated') === 'true';
    if (!isAuthenticated) return;

    // Check if already asked
    const askedBefore = localStorage.getItem('notificationPromptShown');
    if (askedBefore) return;

    // Check if notifications are supported
    if (!pushNotificationManager.isSupported()) return;

    // Check current permission
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return;

    // Show prompt after 30 seconds
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  const handleEnable = async () => {
    const granted = await pushNotificationManager.requestPermission();
    if (granted) {
      pushNotificationManager.showNotification('🎉 Notifications Enabled', {
        body: 'You\'ll now receive order updates and delivery reminders!'
      });
    }
    localStorage.setItem('notificationPromptShown', 'true');
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('notificationPromptShown', 'true');
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        backgroundColor: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        maxWidth: '320px',
        zIndex: 9998,
        animation: 'slideIn 0.3s ease-out'
      }}
    >
      <IconButton
        onClick={handleDismiss}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <NotificationsActiveIcon sx={{ color: '#2e7d32', fontSize: 40 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight="600">
            Enable Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Get order updates & delivery reminders
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={handleEnable}
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: '#2e7d32',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#1b5e20' }
          }}
        >
          Enable
        </Button>
        <Button
          onClick={handleDismiss}
          variant="outlined"
          fullWidth
          sx={{ textTransform: 'none' }}
        >
          Later
        </Button>
      </Box>

      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(400px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default NotificationPrompt;
